import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import config from "../../config/config";
import styles from "./AdvertisementDetail.module.css";
import { SponsorAdvertisementService } from "../../services/sponsor_advertisement_service";
import { SponsorService } from "../../services/sponsor_service";
import { useAdvertisementDetailState } from "../../states/useAdvertisementDetailState";

const AdvertisementDetail = () => {
  const sponsorAdvertisementService = SponsorAdvertisementService();
  const sponsorService = SponsorService();
  const { id } = useParams();
  const navigate = useNavigate();
  const { advertisement, setAdvertisement, sponsor, setSponsor, loading, setLoading, error, setError } = useAdvertisementDetailState();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAdvertisementDetail();
  }, [id]);

  const fetchAdvertisementDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      const adData = await sponsorAdvertisementService.fetchSponsorAdvertisementById(id);
      setAdvertisement(adData);

      if (adData.sponsor) {
        const sponsorData = await sponsorService.fetchSponsorById(adData.sponsor);
        setSponsor(sponsorData);
      }
    } catch (error) {
      console.error("Error fetching advertisement details", error);
      setError("Failed to load advertisement details");
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (iconPath) => {
    if (!iconPath) return "/default-advertisement.png";
    if (iconPath.startsWith("http")) return iconPath;
    return `${config.baseURL}${iconPath}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (startDate && endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      return `Starts: ${formatDate(startDate)}`;
    } else if (endDate) {
      return `Until: ${formatDate(endDate)}`;
    }
    return "No date range specified";
  };

  const isCurrentlyActive = () => {
    if (!advertisement) return false;

    const now = new Date();
    const start = advertisement.start_date ? new Date(advertisement.start_date) : null;
    const end = advertisement.end_date ? new Date(advertisement.end_date) : null;

    if (start && end) {
      return now >= start && now <= end && advertisement.is_active;
    } else if (start) {
      return now >= start && advertisement.is_active;
    } else if (end) {
      return now <= end && advertisement.is_active;
    }
    return advertisement.is_active;
  };

  const handleAdClick = () => {
    if (advertisement?.target_url) {
      window.open(advertisement.target_url, "_blank", "noopener,noreferrer");
    }
  };

  // Function to safely render HTML from CKEditor
  const renderHTML = (htmlString, className = "") => {
    if (!htmlString) {
      return <p className={styles.noDescription}>No description available</p>;
    }

    // Check if the HTML contains any actual content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    const hasContent = tempDiv.textContent.trim().length > 0 ||
      tempDiv.querySelector('img, iframe, video, audio');

    if (!hasContent) {
      return <p className={styles.noDescription}>No description available</p>;
    }

    return (
      <div
        className={`${styles.ckeditorContent} ${className}`}
        dangerouslySetInnerHTML={{ __html: htmlString }}
      />
    );
  };

  // Function to get plain text from HTML for summary
  const getPlainTextFromHTML = (htmlString) => {
    if (!htmlString) return "";

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent.trim();
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Advertisement...</p>
      </div>
    );
  }

  if (error || !advertisement) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h2>Advertisement Not Found</h2>
          <p>
            The advertisement you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/sponsors")}
            className={styles.backButton}
          >
            Back to Sponsors
          </button>
        </div>
      </div>
    );
  }

  const isActive = isCurrentlyActive();
  const descriptionPlainText = getPlainTextFromHTML(advertisement.description);

  return (
    <div className={styles.advertisementDetailPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          {advertisement.ad_type === "video" ? (
            <video
              className={styles.heroMedia}
              autoPlay
              muted
              loop
              playsInline
            >
              <source
                src={getFullImageUrl(advertisement.media_file)}
                type="video/mp4"
              />
            </video>
          ) : (
            <img
              src={getFullImageUrl(advertisement.media_file)}
              alt={advertisement.title}
              className={styles.heroMedia}
            />
          )}
          <div className={styles.heroOverlay}></div>
        </div>

        <div className={styles.heroContent}>
          <div className={styles.container}>
            <button onClick={() => navigate(-1)} className={styles.backButton}>
              ← Back
            </button>

          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className={styles.detailsSection}>
        <div className={styles.container}>
          <div className={styles.detailsGrid}>
            {/* Main Content */}
            <div className={styles.mainContent}>
              {/* Advertisement Details */}
              <div className={styles.descriptionSection}>
                <h2>Campaign Details</h2>
                <div className={styles.eventDescription}>
                  <div className={styles.detailsList}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Title</span>
                      <span className={styles.detailValue}>
                        {advertisement.title || "No title provided"}
                      </span>
                    </div>

                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Description</span>
                      <div className={styles.detailValue}>
                        {renderHTML(advertisement.description)}
                      </div>
                    </div>

                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Campaign Period</span>
                      <div className={styles.detailValue}>
                        {formatDateRange(advertisement.start_date, advertisement.end_date)}
                      </div>
                    </div>

                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Status</span>
                      <span className={`${styles.detailValue} ${isActive ? styles.statusActive : styles.statusInactive
                        }`}>
                        {isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    {advertisement.target_url && (
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Target URL</span>
                        <span className={styles.detailValue}>
                          <a
                            href={advertisement.target_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.urlLink}
                          >
                            {advertisement.target_url}
                          </a>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className={styles.sidebar}>
              {sponsor && (
                <div className={styles.eventInfoCard}>
                  <h3>Sponsor Information</h3>

                  <div className={styles.sponsorPreview}>
                    <img
                      src={getFullImageUrl(sponsor.logo)}
                      alt={sponsor.name}
                      className={styles.sponsorLogo}
                    />
                    <h4 className={styles.sponsorName}>{sponsor.name}</h4>

                    {sponsor.description ? (
                      <div className={styles.sponsorDescription}>
                        {renderHTML(sponsor.description, styles.sponsorDescriptionContent)}
                      </div>
                    ) : (
                      <p className={styles.noDescription}>No description available</p>
                    )}

                    <div className={styles.sponsorActions}>
                      <Link
                        to={`/sponsor/${sponsor.id}`}
                        className={styles.viewSponsorButton}
                      >
                        View Sponsor Profile
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdvertisementDetail;